import React from 'react';
import styled from 'styled-components';
import { Helmet } from 'react-helmet';
import { FaExchangeAlt, FaWallet, FaShoppingCart, FaGlobe, FaRegBuilding, FaCode, FaChevronRight } from 'react-icons/fa';
import SectionTitle from '../components/SectionTitle';
import Button from '../components/Button';

const PageContainer = styled.div`
  padding-top: 2rem;
  background: linear-gradient(135deg, rgba(0, 213, 146, 0.03) 0%, rgba(0, 213, 146, 0) 100%);
  position: relative;
  overflow-x: hidden;
  
  &::before {
    content: '';
    position: fixed;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%2300d592' fill-opacity='0.02'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
    z-index: -1;
    pointer-events: none;
    opacity: 0.8;
  }
`;

const Section = styled.section`
  padding: 6rem 0;
  background-color: ${props => props.alternate ? 'rgba(240, 247, 255, 0.7)' : props.theme.colors.white};
  position: relative;
  overflow: hidden;
  
  ${props => props.alternate && `
    &::before {
      content: '';
      position: absolute;
      width: 100%;
      height: 100%;
      top: 0;
      left: 0;
      background: 
        radial-gradient(circle at 15% 85%, rgba(0, 213, 146, 0.08) 0%, transparent 40%),
        radial-gradient(circle at 85% 15%, rgba(0, 213, 146, 0.08) 0%, transparent 40%);
      z-index: 0;
    }
  `}
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const EcosystemGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  gap: 2.5rem;
  margin-top: 4rem;
  position: relative;
  z-index: 2;
`;

const EcosystemCard = styled.div`
  background: linear-gradient(135deg, #ffffff 0%, #f8fcff 100%);
  border-radius: 20px;
  padding: 2.5rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.04);
  transition: all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1);
  position: relative;
  border: 1px solid rgba(0, 213, 146, 0.1);
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, rgba(0, 213, 146, 0.05) 0%, transparent 100%);
    opacity: 0;
    transition: opacity 0.4s ease;
    z-index: 0;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: -100px;
    right: -100px;
    width: 200px;
    height: 200px;
    background: radial-gradient(circle, rgba(0, 213, 146, 0.08) 0%, transparent 70%);
    border-radius: 50%;
    opacity: 0.5;
    transition: all 0.4s ease;
  }
  
  &:hover {
    transform: translateY(-10px) scale(1.02);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.08);
    border-color: rgba(0, 213, 146, 0.3);
    
    &::before {
      opacity: 1;
    }
    
    &::after {
      transform: translate(-30px, -30px);
    }
  }
`;

const EcosystemIcon = styled.div`
  width: 70px;
  height: 70px;
  background: linear-gradient(135deg, ${props => props.theme.colors.primary}, #00a080);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.8rem;
  color: white;
  font-size: 1.8rem;
  position: relative;
  box-shadow: 0 10px 20px rgba(0, 213, 146, 0.2);
  
  &::before {
    content: '';
    position: absolute;
    top: -5px;
    left: -5px;
    right: -5px;
    bottom: -5px;
    background: linear-gradient(135deg, ${props => props.theme.colors.primary}, #00a080);
    border-radius: 18px;
    opacity: 0.3;
    z-index: -1;
    filter: blur(8px);
  }
  
  svg {
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
  }
`;

const EcosystemTitle = styled.h3`
  font-size: 1.4rem;
  margin-bottom: 1rem;
  color: ${props => props.theme.colors.secondary};
  font-weight: 600;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -6px;
    left: 0;
    width: 40px;
    height: 2px;
    background-color: ${props => props.theme.colors.primary};
    border-radius: 2px;
  }
`;

const EcosystemDescription = styled.p`
  color: ${props => props.theme.colors.gray};
  font-size: 1rem;
  line-height: 1.7;
  margin-bottom: 1.8rem;
  position: relative;
  z-index: 1;
`;

const PartnerSection = styled.section`
  padding: 6rem 0;
  background: linear-gradient(135deg, rgba(240, 247, 255, 0.8) 0%, rgba(255, 255, 255, 0.9) 100%);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    background: 
      radial-gradient(circle at 20% 80%, rgba(0, 213, 146, 0.07) 0%, transparent 40%),
      radial-gradient(circle at 80% 20%, rgba(0, 213, 146, 0.07) 0%, transparent 40%);
    z-index: 0;
  }
`;

const PartnerGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 2.5rem;
  margin-top: 4rem;
  position: relative;
  z-index: 1;
`;

const PartnerCard = styled.div`
  background: #ffffff;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.03);
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  aspect-ratio: 1/1;
  border: 1px solid rgba(0, 213, 146, 0.1);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, rgba(0, 213, 146, 0.05) 0%, transparent 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  &:hover {
    transform: translateY(-8px);
    border-color: rgba(0, 213, 146, 0.3);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.07);
    
    &::before {
      opacity: 1;
    }
  }
`;

const PartnerLogo = styled.div`
  width: 90px;
  height: 90px;
  background: linear-gradient(135deg, rgba(0, 213, 146, 0.1) 0%, rgba(0, 213, 146, 0.05) 100%);
  border: 1px solid rgba(0, 213, 146, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.2rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1.5rem;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    border-radius: 50%;
    border: 1px dashed rgba(0, 213, 146, 0.3);
    top: 0;
    left: 0;
    animation: spin 20s linear infinite;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const PartnerName = styled.h3`
  font-size: 1.2rem;
  color: ${props => props.theme.colors.secondary};
  position: relative;
  padding-bottom: 0.8rem;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 30px;
    height: 2px;
    background: ${props => props.theme.colors.primary};
    border-radius: 2px;
  }
`;

const TokenomicsContainer = styled.div`
  margin: 4rem 0;
  position: relative;
`;

const TokenomicsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
  position: relative;
  z-index: 2;
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

const TokenomicsContent = styled.div`
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    width: 150px;
    height: 150px;
    background: radial-gradient(circle, rgba(0, 213, 146, 0.1) 0%, transparent 70%);
    border-radius: 50%;
    top: -30px;
    left: -30px;
    z-index: -1;
    opacity: 0.7;
  }
`;

const TokenomicsTitle = styled.h3`
  font-size: 1.8rem;
  margin-bottom: 1.5rem;
  color: ${props => props.theme.colors.secondary};
  font-weight: 600;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 0;
    width: 50px;
    height: 3px;
    background: ${props => props.theme.colors.primary};
    border-radius: 2px;
  }
`;

const TokenomicsDescription = styled.p`
  color: ${props => props.theme.colors.gray};
  font-size: 1.05rem;
  line-height: 1.8;
  margin-bottom: 2rem;
`;

const TokenomicsStats = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.8rem;
`;

const TokenomicsStat = styled.div`
  background: linear-gradient(135deg, #ffffff 0%, #f8fcff 100%);
  border-radius: 16px;
  padding: 1.8rem;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(0, 213, 146, 0.1);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    width: 80px;
    height: 80px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(0, 213, 146, 0.1) 0%, transparent 70%);
    bottom: -20px;
    right: -20px;
    opacity: 0.7;
  }
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.07);
    border-color: rgba(0, 213, 146, 0.3);
  }
`;

const TokenomicsStatTitle = styled.h4`
  font-size: 0.95rem;
  color: ${props => props.theme.colors.gray};
  margin-bottom: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 1px;
  display: flex;
  align-items: center;
  
  &::before {
    content: '';
    display: inline-block;
    width: 8px;
    height: 8px;
    background-color: ${props => props.theme.colors.primary};
    border-radius: 50%;
    margin-right: 8px;
  }
`;

const TokenomicsStatValue = styled.p`
  font-size: 1.8rem;
  font-weight: 700;
  color: ${props => props.theme.colors.secondary};
  background: linear-gradient(90deg, ${props => props.theme.colors.secondary}, #354F6B);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const TokenomicsChartContainer = styled.div`
  background: linear-gradient(135deg, #ffffff 0%, #f8fcff 100%);
  border-radius: 20px;
  padding: 2.5rem;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(0, 213, 146, 0.1);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, rgba(0, 213, 146, 0.05) 0%, transparent 100%);
  }
  
  &:hover {
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.08);
    border-color: rgba(0, 213, 146, 0.2);
  }
`;

const TokenomicsChartTitle = styled.h3`
  font-size: 1.4rem;
  margin-bottom: 2rem;
  color: ${props => props.theme.colors.secondary};
  text-align: center;
  font-weight: 600;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 50px;
    height: 3px;
    background: ${props => props.theme.colors.primary};
    border-radius: 2px;
  }
`;

const TokenomicsChartWrapper = styled.div`
  height: 300px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
  z-index: 2;
`;

const TokenomicsDistribution = styled.div`
  display: flex;
  margin-bottom: 1.2rem;
  align-items: center;
  position: relative;
  padding: 0.5rem 0.8rem;
  border-radius: 10px;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(0, 213, 146, 0.05);
    transform: translateX(5px);
  }
`;

const TokenomicsDistributionColor = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 8px;
  background-color: ${props => props.color};
  margin-right: 1.2rem;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: hidden;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.3) 0%, transparent 100%);
  }
`;

const TokenomicsDistributionLabel = styled.span`
  flex: 2;
  color: ${props => props.theme.colors.secondary};
  font-weight: 500;
  font-size: 1.05rem;
`;

const TokenomicsDistributionValue = styled.span`
  flex: 1;
  color: ${props => props.theme.colors.secondary};
  font-weight: 700;
  text-align: right;
  background: linear-gradient(90deg, ${props => props.theme.colors.secondary}, #354F6B);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-size: 1.05rem;
`;

const TokenomicsBar = styled.div`
  height: 35px;
  background: linear-gradient(to right, 
    #00d592 0%, #00d592 40%, 
    #3498db 40%, #3498db 60%, 
    #9b59b6 60%, #9b59b6 75%, 
    #e74c3c 75%, #e74c3c 85%, 
    #f1c40f 85%, #f1c40f 100%
  );
  border-radius: 10px;
  margin: 2.5rem 0;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: hidden;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(to bottom, 
      rgba(255, 255, 255, 0.2) 0%, 
      transparent 100%
    );
  }
`;

const LearnMoreSection = styled.section`
  padding: 6rem 0;
  background: linear-gradient(135deg, #f0fffa 0%, #e6f7f2 100%);
  text-align: center;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    background: 
      radial-gradient(circle at 30% 40%, rgba(0, 213, 146, 0.08) 0%, transparent 50%),
      radial-gradient(circle at 70% 60%, rgba(0, 213, 146, 0.08) 0%, transparent 50%);
    z-index: 0;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 10px;
    background: linear-gradient(90deg, 
      ${props => props.theme.colors.primary} 0%, 
      transparent 100%
    );
    opacity: 0.3;
  }
`;

const LearnMoreTitle = styled.h2`
  font-size: 2.5rem;
  margin-bottom: 1.5rem;
  color: ${props => props.theme.colors.secondary};
  font-weight: 700;
  position: relative;
  z-index: 2;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 100px;
    height: 3px;
    background: linear-gradient(90deg, 
      transparent 0%,
      ${props => props.theme.colors.primary} 50%,
      transparent 100%
    );
  }
`;

const LearnMoreDescription = styled.p`
  font-size: 1.2rem;
  margin-bottom: 2.5rem;
  max-width: 750px;
  margin-left: auto;
  margin-right: auto;
  color: ${props => props.theme.colors.gray};
  line-height: 1.8;
  position: relative;
  z-index: 2;
`;

const Ecosystem = () => {
  return (
    <PageContainer>
      <Helmet>
        <title>Ecosystem | RDT Chain - DApps, Tokenomics & Partners</title>
      </Helmet>
      <Section>
        <Container>
          <SectionTitle
            subtitle="RDT Chain Ecosystem"
            title="Building a Complete Financial System"
            description="Explore the growing ecosystem of applications, services, and tools built on RDT Chain."
            center
          />

          <EcosystemGrid>
            <EcosystemCard>
              <EcosystemIcon>
                <FaExchangeAlt />
              </EcosystemIcon>
              <EcosystemTitle>Decentralized Exchanges</EcosystemTitle>
              <EcosystemDescription>
                Trade tokens directly from your wallet with no intermediaries. Enjoy extremely low fees and fast transaction speeds on RDT Chain DEXes.
              </EcosystemDescription>
            </EcosystemCard>

            <EcosystemCard>
              <EcosystemIcon>
                <FaWallet />
              </EcosystemIcon>
              <EcosystemTitle>Wallets</EcosystemTitle>
              <EcosystemDescription>
                Compatible with all major EVM wallets including MetaMask, Trust Wallet, and more. Manage your RDT tokens and interact with dApps seamlessly.
              </EcosystemDescription>
            </EcosystemCard>

            <EcosystemCard>
              <EcosystemIcon>
                <FaShoppingCart />
              </EcosystemIcon>
              <EcosystemTitle>Payment Solutions</EcosystemTitle>
              <EcosystemDescription>
                Integrate zero-fee payment solutions for e-commerce and retail. Accept RDT payments without worrying about transaction costs.
              </EcosystemDescription>
            </EcosystemCard>

            <EcosystemCard>
              <EcosystemIcon>
                <FaGlobe />
              </EcosystemIcon>
              <EcosystemTitle>Global Remittance</EcosystemTitle>
              <EcosystemDescription>
                Enable instantaneous cross-border remittances with zero transaction fees, allowing efficient global value transfer without intermediaries.
              </EcosystemDescription>
            </EcosystemCard>

            <EcosystemCard>
              <EcosystemIcon>
                <FaRegBuilding />
              </EcosystemIcon>
              <EcosystemTitle>Enterprise Solutions</EcosystemTitle>
              <EcosystemDescription>
                Custom blockchain solutions for businesses looking to leverage RDT Chain's zero-fee technology for cross-border payments.
              </EcosystemDescription>
            </EcosystemCard>

            <EcosystemCard>
              <EcosystemIcon>
                <FaCode />
              </EcosystemIcon>
              <EcosystemTitle>Developer Tools</EcosystemTitle>
              <EcosystemDescription>
                Comprehensive tooling for building dApps on RDT Chain, including SDKs, APIs, and development frameworks.
              </EcosystemDescription>
            </EcosystemCard>
          </EcosystemGrid>
        </Container>
      </Section>

      <TokenomicsContainer>
        <Container>
          <SectionTitle
            subtitle="Tokenomics"
            title="RDT Token Economics"
            description="Understanding the distribution and utility of the RDT token."
            center
          />

          <TokenomicsGrid>
            <TokenomicsContent>
              <TokenomicsTitle>RDT Token Overview</TokenomicsTitle>
              <TokenomicsDescription>
                The RDT token is the native currency of the RDT Chain blockchain. With a total supply of 25,000 Crore (250 billion) tokens,
                RDT is designed to facilitate fee-less transactions while maintaining the security and incentive structure of the network.
              </TokenomicsDescription>

              <TokenomicsStats>
                <TokenomicsStat>
                  <TokenomicsStatTitle>Total Supply</TokenomicsStatTitle>
                  <TokenomicsStatValue>25,000 Crore</TokenomicsStatValue>
                </TokenomicsStat>

                <TokenomicsStat>
                  <TokenomicsStatTitle>Circulating Supply</TokenomicsStatTitle>
                  <TokenomicsStatValue>25,000 Crore</TokenomicsStatValue>
                </TokenomicsStat>

                <TokenomicsStat>
                  <TokenomicsStatTitle>Transaction Fee</TokenomicsStatTitle>
                  <TokenomicsStatValue>0 RDT</TokenomicsStatValue>
                </TokenomicsStat>

                <TokenomicsStat>
                  <TokenomicsStatTitle>Block Time</TokenomicsStatTitle>
                  <TokenomicsStatValue>5 seconds</TokenomicsStatValue>
                </TokenomicsStat>
              </TokenomicsStats>
            </TokenomicsContent>

            <TokenomicsChartContainer>
              <TokenomicsChartTitle>Token Distribution</TokenomicsChartTitle>
              <TokenomicsChartWrapper>
                <TokenomicsBar />

                <TokenomicsDistribution>
                  <TokenomicsDistributionColor color="#00d592" />
                  <TokenomicsDistributionLabel>Ecosystem & Development</TokenomicsDistributionLabel>
                  <TokenomicsDistributionValue>40%</TokenomicsDistributionValue>
                </TokenomicsDistribution>

                <TokenomicsDistribution>
                  <TokenomicsDistributionColor color="#3498db" />
                  <TokenomicsDistributionLabel>Public Sale</TokenomicsDistributionLabel>
                  <TokenomicsDistributionValue>20%</TokenomicsDistributionValue>
                </TokenomicsDistribution>

                <TokenomicsDistribution>
                  <TokenomicsDistributionColor color="#9b59b6" />
                  <TokenomicsDistributionLabel>Team & Advisors</TokenomicsDistributionLabel>
                  <TokenomicsDistributionValue>15%</TokenomicsDistributionValue>
                </TokenomicsDistribution>

                <TokenomicsDistribution>
                  <TokenomicsDistributionColor color="#e74c3c" />
                  <TokenomicsDistributionLabel>Marketing & Partnerships</TokenomicsDistributionLabel>
                  <TokenomicsDistributionValue>10%</TokenomicsDistributionValue>
                </TokenomicsDistribution>

                <TokenomicsDistribution>
                  <TokenomicsDistributionColor color="#f1c40f" />
                  <TokenomicsDistributionLabel>Reserve Fund</TokenomicsDistributionLabel>
                  <TokenomicsDistributionValue>15%</TokenomicsDistributionValue>
                </TokenomicsDistribution>
              </TokenomicsChartWrapper>
            </TokenomicsChartContainer>
          </TokenomicsGrid>
        </Container>
      </TokenomicsContainer>

      <PartnerSection>
        <Container>
          <SectionTitle
            subtitle="Our Partners"
            title="Strategic Collaborations"
            description="Working with leading organizations to expand the RDT Chain ecosystem."
            center
          />

          <PartnerGrid>
            <PartnerCard>
              <PartnerLogo>P1</PartnerLogo>
              <PartnerName>EVM Alliance</PartnerName>
            </PartnerCard>

            <PartnerCard>
              <PartnerLogo>P2</PartnerLogo>
              <PartnerName>PaymentTech</PartnerName>
            </PartnerCard>

            <PartnerCard>
              <PartnerLogo>P3</PartnerLogo>
              <PartnerName>CrossPay</PartnerName>
            </PartnerCard>

            <PartnerCard>
              <PartnerLogo>P4</PartnerLogo>
              <PartnerName>BlockVentures</PartnerName>
            </PartnerCard>

            <PartnerCard>
              <PartnerLogo>P5</PartnerLogo>
              <PartnerName>ChainSecure</PartnerName>
            </PartnerCard>

            <PartnerCard>
              <PartnerLogo>P6</PartnerLogo>
              <PartnerName>MetaWallet</PartnerName>
            </PartnerCard>
          </PartnerGrid>
        </Container>
      </PartnerSection>

      <LearnMoreSection>
        <Container>
          <LearnMoreTitle>Join the RDT Chain Ecosystem</LearnMoreTitle>
          <LearnMoreDescription>
            Whether you're a developer, business, or user, there's a place for you in the growing RDT Chain ecosystem.
          </LearnMoreDescription>
          <Button href="https://explorer.rdtchain.com" target="_blank" rel="noopener noreferrer">
            Explore RDT Chain
          </Button>
        </Container>
      </LearnMoreSection>
    </PageContainer>
  );
};

export default Ecosystem;